// Enhanced Permission Initialization - NO FALLBACK SYSTEM
// Only uses backend API response, never fallback permissions

import { usePermissionStore } from '../permissions';
import { userGroupsService } from '../../services/userGroupsService';
import { PermissionMap, UserGroup } from '../../types/access';
import { getCurrentConfig } from '../../config';
import { mergePermissions } from './mergePermissions';
import toast from 'react-hot-toast';

/**
 * Initialize permissions using ONLY backend API - NO FALLBACK
 * Completely removes development fallback system
 * 
 * @param isProduction - Legacy parameter (ignored)
 * @param getAccessTokenFn - Legacy parameter (ignored) 
 * @param account - MSAL account object for extracting user info
 * @param graphApiGroups - User groups from Microsoft Graph API
 */
export const initPermissions = async (
  isProduction?: boolean,
  getAccessTokenFn?: () => Promise<string | null>,
  account?: any,
  graphApiGroups?: any[]
) => {
  const store = usePermissionStore.getState();
  
  try {
    // Initializing permissions - BACKEND API ONLY
    store.setLoading(true);
    store.clearError();

    // STEP 1: Get user's Azure group IDs from Graph API response
    let userAzureGroupIds: string[] = [];
    
    if (graphApiGroups && graphApiGroups.length > 0) {
      // Using Graph API groups for backend matching
      
      // Extract Azure Group IDs from Graph API response
      userAzureGroupIds = graphApiGroups.map((group: any) => group.id || group.objectId).filter(Boolean);
      // Extracted Azure Group IDs from Graph API
      
      // Log group details for debugging - available
    } else {
      // No Graph API groups provided, trying fallback from ID token
      
      // Fallback: Try to get from ID token (original logic)
      const msalIdTokenKeys = Object.keys(sessionStorage).filter(key => 
        key.includes('idtoken')
      );
      
      if (msalIdTokenKeys.length > 0) {
        try {
          const tokenData = JSON.parse(sessionStorage.getItem(msalIdTokenKeys[0]) || '{}');
          if (tokenData.secret) {
            const payload = JSON.parse(atob(tokenData.secret.split('.')[1]));
            userAzureGroupIds = payload.groups || [];
            // Fallback: User Azure Groups from ID token
          }
        } catch (e) {
          console.error('Failed to extract groups from ID token:', e);
        }
      }
    }

    // Get current user info from the MSAL account
    let currentUser = 'unknown@user.com'; // fallback
    
    if (account && account.name) {
      // Prioritize the friendly name over email
      currentUser = account.name;
    } else if (account && account.username) {
      // Use email if name not available
      currentUser = account.username;
    } else {
      // Try to get from permission store if account not available
      currentUser = store.currentUser || 'unknown@user.com';
    }
    // Current User available

    // STEP 2: Call backend API to get user permissions
    // Calling backend API for user groups and permissions
    const userGroupsResult = await userGroupsService.fetchUserGroupsAndPermissions(userAzureGroupIds);

    // STEP 3: Validate API response
    if (!userGroupsResult || !userGroupsResult.userGroup) {
      console.error('❌ Backend API did not return valid user group data');
      throw new Error('Backend API failed to return user group information');
    }

    if (!userGroupsResult.permissions) {
      console.error('❌ Backend API did not return permissions');
      throw new Error('Backend API failed to return permissions');
    }

    // Backend API Response received
    
    // STEP 4: Set permissions directly from backend (NO PROCESSING, NO FALLBACK)
    store.setPermissions(userGroupsResult.permissions);
    store.setUserGroups([userGroupsResult.userGroup]);
    store.setCurrentUser(currentUser);
    store.setNoGroups(false);
    
    // Set environment to show this came from backend API
    const config = getCurrentConfig();
    const environment = config?.environment || 'backend-api';
    store.setEnvironmentInfo(environment, new Date().toISOString());

    // Permissions initialized successfully from backend API
    
    // Show success message with user group info
    toast.success(`Permissions loaded for: ${userGroupsResult.userGroup.displayName}`, { 
      duration: 3000 
    });

    // Final validation log
    const finalStats = store.getPermissionStats();
    console.log('📊 Final Permission Stats:', finalStats);

  } catch (error) {
    console.error('❌ Permission initialization failed:', error);
    
    // Handle error properly for TypeScript
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    
    // NO FALLBACK - Just set error state
    store.setError(`Permission initialization failed: ${errorMessage}`);
    store.setNoGroups(true);
    store.setLoading(false);
    
    toast.error('Failed to load permissions. Please try logging out and back in.', { 
      duration: 5000 
    });
    
    throw error;
  } finally {
    store.setLoading(false);
  }
};